import {
  FlexColumn,
  FlexRow,
  LabeledData,
  MaterialIcon,
  MaterialIcons,
  useMoment,
  VerticalDivider,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { StatefulPopover } from 'baseui/popover';
import { LabelMedium, ParagraphSmall } from 'baseui/typography';
import { memo, useMemo } from 'react';
import { Marker } from 'react-map-gl/mapbox';
import { generatePath } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { GPT_DETAIL_PAGE } from '../../../constants/app';
import { useCamera } from '../../../hooks/useCamera';
import { useStyles } from '../../../hooks/useStyles';
import { workspaceState } from '../../../storage/app';
import { gptAlarmTypeIconState, gptAlarmTypeNameState, gptAlarmTypeState } from '../../../storage/gptStates';
import { EventAlarm, EventCore, LastAlarm } from '../../../types/gptTypes';
import { isValidSafegptAlarmPoint } from '../../../utils/map';
import SafeGptAlarmIcon from '../../shared/SafeGptAlarmIcon';

type Props = {
  alarm: EventAlarm | LastAlarm;
  event?: EventCore;
};

function GptEventAlarmMarker({ alarm, event }: Readonly<Props>) {
  const { theme, css } = useStyles();
  const { zoomTo } = useCamera();
  const { formatDateFromTs } = useMoment();
  const alarmType = useRecoilValue(gptAlarmTypeNameState(alarm.alarmType));
  const alarmData = useRecoilValue(gptAlarmTypeState(alarm.alarmType));
  const alarmIcon = useRecoilValue(gptAlarmTypeIconState(alarm.alarmType));
  const workspace = useRecoilValue(workspaceState);
  const iconWrapper = css({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    height: '25px',
    width: '25px',
    borderRadius: '50%',
    background: alarmData?.color || '#B00000',
    paddingBottom: '3px',
    boxSizing: 'border-box',
  });
  const icon = useMemo(
    () =>
      alarmIcon ? (
        <SafeGptAlarmIcon
          url={alarmIcon}
          size="16px"
        />
      ) : (
        <span className={iconWrapper}>
          <MaterialIcons.Warning
            size="16px"
            color="#FFF"
          />
        </span>
      ),
    [iconWrapper, alarmIcon],
  );

  if (!alarm) return null;

  if (!isValidSafegptAlarmPoint(alarm.gpsLng, alarm.gpsLat)) return null;

  const onClick = () => {
    zoomTo({
      lat: alarm.gpsLat,
      lng: alarm.gpsLng,
    });
  };

  return (
    <Marker
      longitude={alarm.gpsLng}
      latitude={alarm.gpsLat}
      onClick={!event ? onClick : undefined}
    >
      {event && (
        <StatefulPopover
          showArrow
          autoFocus={false}
          returnFocus
          placement="top"
          triggerType="hover"
          popoverMargin={0}
          content={({ close }) => (
            <FlexColumn
              gap={theme.sizing.scale300}
              classNames={css({
                paddingRight: theme.sizing.scale600,
                paddingLeft: theme.sizing.scale600,
                paddingTop: theme.sizing.scale500,
                paddingBottom: theme.sizing.scale500,
              })}
            >
              <FlexRow gap={theme.sizing.scale200}>
                <LabelMedium>{event.ident}</LabelMedium>
                <ParagraphSmall
                  margin={0}
                  color={theme.colors.contentSecondary}
                >
                  {`- ${event.eventName}`}
                </ParagraphSmall>
              </FlexRow>

              <FlexColumn gap={theme.sizing.scale0}>
                <LabeledData
                  label={'Última alarma:'}
                  content={alarmType}
                />
                <LabeledData
                  label={'Fecha:'}
                  content={formatDateFromTs(+alarm.startTime)}
                />
              </FlexColumn>
              <FlexRow
                classNames={css({
                  alignItems: 'center',
                  justifyContent: 'space-evenly',
                })}
              >
                <Button
                  shape="circle"
                  kind="tertiary"
                  size="compact"
                  onClick={() => {
                    if (workspace) {
                      window.open(
                        generatePath(GPT_DETAIL_PAGE.route, { id: workspace._id, eventId: event._id }),
                        '_blank',
                        'directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no, height=900px, width=1400px',
                      );
                    }
                  }}
                >
                  <MaterialIcon
                    name="open_in_new"
                    size="compact"
                  />
                </Button>
                <VerticalDivider />
                <Button
                  shape="circle"
                  kind="tertiary"
                  size="compact"
                  onClick={() => {
                    close();
                    zoomTo({
                      lat: alarm.gpsLat,
                      lng: alarm.gpsLng,
                    });
                  }}
                >
                  <MaterialIcon
                    name="zoom_in_map"
                    size="compact"
                  />
                </Button>
              </FlexRow>
            </FlexColumn>
          )}
          overrides={{
            Inner: {
              style: {
                backgroundColor: theme.colors.backgroundPrimary,
              },
            },
            Arrow: {
              style: {
                backgroundColor: theme.colors.backgroundPrimary,
              },
            },
          }}
        >
          {icon}
        </StatefulPopover>
      )}
      {!event && icon}
    </Marker>
  );
}

export default memo(GptEventAlarmMarker);
